import { type FragmentOf, graphql, readFragment } from "@opensea/graphql"
import { Link } from "@opensea/next-ui-kit"
import { Chip, type ChipProps } from "@opensea/ui-kit"
import { useTranslations } from "next-intl"
import { DropStatus } from "@/components/collection/drops/constants"
import {
  getDropStatus,
  getDropStatusFragment,
} from "@/components/collection/drops/utils"
import {
  collectionUrlFragment,
  getCollectionUrl,
} from "@/components/collection/utils"

export const CollectionDropStatusChipFragment = graphql(
  `
    fragment CollectionDropStatusChip on Collection {
      ...collectionUrl
      ...getDropStatus
    }
  `,
  [collectionUrlFragment, getDropStatusFragment],
)

type Props = {
  collection: FragmentOf<typeof CollectionDropStatusChipFragment>
} & Omit<ChipProps, "children">

export function CollectionDropStatusChip({
  collection: collectionKey,
  ...rest
}: Props) {
  const collection = readFragment(
    CollectionDropStatusChipFragment,
    collectionKey,
  )
  const t = useTranslations("CollectionDropStatusChip")

  const status = getDropStatus(collection)

  if (!status) {
    return null
  }

  const getChipProps = () => {
    switch (status) {
      case DropStatus.MINTING:
        return {
          text: t("mintingNow"),
          className: "border-success-1 text-success-1",
        }
      case DropStatus.MINTED_OUT:
      case DropStatus.MINT_ENDED:
        return {
          text: t("mintEnded"),
          className: "border-border-1 text-text-secondary",
        }
      case DropStatus.MINTING_SOON:
        return {
          text: t("mintingSoon"),
          className: "border-blue-1 text-blue-1",
        }
      default:
        return null
    }
  }

  const chipProps = getChipProps()
  if (!chipProps) {
    return null
  }

  return (
    <Link
      href={{
        pathname: `${getCollectionUrl(collection)}/overview`,
      }}
      variant="unstyled"
    >
      <Chip className={chipProps.className} {...rest}>
        {chipProps.text}
      </Chip>
    </Link>
  )
}
